import config from '@/config';
import {
  AuthSessionExpiredError,
  fetchCurrentUser,
  fetchWithKeycloakToken,
  getKeycloakRoles,
  handleAuthSessionExpired,
} from '@/features/Keycloak/keycloakService';
import type { TypedDocumentString } from '@/generated/graphql';
import { GraphQLError } from 'graphql';

type AuditPolicy = 'required' | 'exempt';

interface GraphqlResponseError {
  message: string;
  extensions?: Record<string, unknown>;
}

interface GraphqlResponse<TResult> {
  data?: TResult;
  errors?: GraphqlResponseError[];
}

function isAuthHookUnauthorizedError(error: GraphqlResponseError): boolean {
  return (
    error.message === 'Authentication hook unauthorized this request' &&
    error.extensions?.code === 'access-denied'
  );
}

function toGraphQLError(error: GraphqlResponseError): GraphQLError {
  return new GraphQLError(error.message, {
    extensions: error.extensions,
  });
}

async function handleGraphqlError(error: GraphqlResponseError): Promise<never> {
  if (isAuthHookUnauthorizedError(error)) {
    try {
      await fetchCurrentUser(config.rest.url);
    } catch {
      await handleAuthSessionExpired();
      throw new AuthSessionExpiredError();
    }
  }

  throw toGraphQLError(error);
}

// Case 1: No variables expected
export async function execute<TResult>(options: {
  query: TypedDocumentString<TResult, Record<string, never>>;
  auditPolicy?: AuditPolicy;
  role?: string;
}): Promise<TResult>;

// Case 2: Variables expected
export async function execute<
  TResult,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TVariables extends Record<string, any>,
>(options: {
  query: TypedDocumentString<TResult, TVariables>;
  variables: TVariables extends Record<string, never> ? never : TVariables;
  auditPolicy?: AuditPolicy;
  role?: string;
}): Promise<TResult>;

// Case 3: Variables expected
export async function execute<
  TResult,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TVariables extends Record<string, any>,
>(options: {
  query: TypedDocumentString<TResult, TVariables>;
  variables: TVariables extends Record<string, never> ? never : TVariables;
  auditPolicy?: AuditPolicy;
  role?: string;
}): Promise<TResult>;

// Implementation
export async function execute<TResult, TVariables>(options: {
  query: TypedDocumentString<TResult, TVariables>;
  variables?: TVariables;
  auditPolicy?: AuditPolicy;
  role?: string;
}): Promise<TResult> {
  const { query, variables, auditPolicy = 'required', role } = options;

  const roles = getKeycloakRoles();

  if (roles?.length !== 1) {
    console.error('Must have exactly one role for each user for now.');
    throw new Error('Must have exactly one role for each user for now.');
  }

  const queryString = query.toString();
  const isMutation = queryString.includes('mutation');
  const auditLogMutationIncluded =
    queryString.includes('insertAuditLogOne') ||
    queryString.includes('insertAuditLog');

  if (isMutation && auditPolicy !== 'exempt' && !auditLogMutationIncluded) {
    throw new Error('AuditLog operation should be included in the mutation');
  }

  const response = await fetchWithKeycloakToken(config.graphql.url, {
    method: 'POST',
    headers: {
      Accept: 'application/graphql-response+json',
      'Content-Type': 'application/json',
      // Don't use this except for debugging
      // 'x-hasura-admin-secret': atob(
      //   import.meta.env.VITE_HASURA_ADMIN_SECRET_B64,
      // ),
      'x-hasura-role': role || roles[0],
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const res = (await response.json()) as GraphqlResponse<TResult>;
  if (res.errors?.[0]) {
    await handleGraphqlError(res.errors[0]);
  }

  if (res.data === undefined) {
    throw new Error('GraphQL response missing data');
  }

  return res.data;
}
